import React, { useState, useRef } from 'react';
import { Container, Row, Col, Card, Form, Button, Table, Alert, ProgressBar, Badge } from 'react-bootstrap';
import * as XLSX from 'xlsx';
import { toast } from 'react-toastify';

const OutstandingUpload = () => {
  const [uploadStep, setUploadStep] = useState('upload'); // upload, mapping, processing, complete
  const [uploadedData, setUploadedData] = useState([]);
  const [headers, setHeaders] = useState([]);
  const [columnMapping, setColumnMapping] = useState({});
  const [validationResults, setValidationResults] = useState([]);
  const [processingProgress, setProcessingProgress] = useState(0);
  const [uploadResults, setUploadResults] = useState(null);
  const fileInputRef = useRef(null);

  const requiredColumns = [
    { key: 'retailer_id', label: 'Retailer ID', example: 'RET001' },
    { key: 'retailer_name', label: 'Retailer Name', example: 'Smart Mobile Store' },
    { key: 'outstanding_amount', label: 'Outstanding Amount', example: '25000' },
    { key: 'due_date', label: 'Due Date', example: '2024-01-15' },
    { key: 'invoice_number', label: 'Invoice Number', example: 'INV001' },
    { key: 'description', label: 'Description', example: 'Monthly recharge' }
  ];

  const sampleData = [
    {
      'Retailer ID': 'RET001',
      'Store Name': 'Smart Mobile Store',
      'Outstanding': '25000',
      'Due Date': '2024-01-15',
      'Invoice': 'INV001',
      'Notes': 'Monthly recharge outstanding'
    },
    {
      'Retailer ID': 'RET002',
      'Store Name': 'Tech Zone Electronics',
      'Outstanding': '18000',
      'Due Date': '2024-01-20',
      'Invoice': 'INV002',
      'Notes': 'Quarterly payment pending'
    }
  ];

  const handleFileUpload = (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.name.match(/\.(xlsx|xls|csv)$/)) {
      toast.error('Please select a valid Excel file (.xlsx, .xls) or CSV file');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const data = new Uint8Array(e.target.result);
        const workbook = XLSX.read(data, { type: 'array' });
        const sheetName = workbook.SheetNames[0];
        const worksheet = workbook.Sheets[sheetName];
        const jsonData = XLSX.utils.sheet_to_json(worksheet);

        if (jsonData.length === 0) {
          toast.error('The file appears to be empty or has no valid data');
          return;
        }

        const fileHeaders = Object.keys(jsonData[0]);
        setHeaders(fileHeaders);
        setUploadedData(jsonData);
        setUploadStep('mapping');
        toast.success(`File uploaded successfully. Found ${jsonData.length} records.`);
      } catch (error) {
        toast.error('Error reading file. Please ensure it\'s a valid Excel or CSV file.');
      }
    };
    reader.readAsArrayBuffer(file);
  };

  const handleColumnMapping = (requiredField, selectedColumn) => {
    setColumnMapping(prev => ({
      ...prev,
      [requiredField]: selectedColumn
    }));
  };

  const validateMapping = () => {
    const errors = [];
    
    // Check if all required columns are mapped
    requiredColumns.forEach(col => {
      if (!columnMapping[col.key]) {
        errors.push(`${col.label} must be mapped to a column`);
      }
    });

    if (errors.length > 0) {
      toast.error('Please map all required columns before proceeding');
      return false;
    }

    return true;
  };

  const validateData = () => {
    const validationErrors = [];
    const validData = [];

    uploadedData.forEach((row, index) => {
      const rowErrors = [];
      const mappedRow = {};

      // Map columns based on user selection
      requiredColumns.forEach(col => {
        const sourceColumn = columnMapping[col.key];
        if (sourceColumn) {
          mappedRow[col.key] = row[sourceColumn];
        }
      });

      // Validate each field
      if (!mappedRow.retailer_id || mappedRow.retailer_id.toString().trim() === '') {
        rowErrors.push('Retailer ID is required');
      }

      if (!mappedRow.retailer_name || mappedRow.retailer_name.toString().trim() === '') {
        rowErrors.push('Retailer Name is required');
      }

      if (!mappedRow.outstanding_amount || isNaN(parseFloat(mappedRow.outstanding_amount))) {
        rowErrors.push('Outstanding Amount must be a valid number');
      } else if (parseFloat(mappedRow.outstanding_amount) <= 0) {
        rowErrors.push('Outstanding Amount must be greater than 0');
      }

      if (mappedRow.due_date) {
        const dueDate = new Date(mappedRow.due_date);
        if (isNaN(dueDate.getTime())) {
          rowErrors.push('Due Date must be a valid date');
        }
      }

      if (rowErrors.length > 0) {
        validationErrors.push({
          row: index + 1,
          errors: rowErrors,
          data: mappedRow
        });
      } else {
        validData.push({
          ...mappedRow,
          outstanding_amount: parseFloat(mappedRow.outstanding_amount),
          due_date: mappedRow.due_date ? new Date(mappedRow.due_date).toISOString().split('T')[0] : null
        });
      }
    });

    setValidationResults(validationErrors);
    return validData;
  };

  const processMapping = () => {
    if (!validateMapping()) return;

    const validData = validateData();
    
    if (validationResults.length > 0) {
      toast.warning(`Found ${validationResults.length} validation errors. Please review and fix them.`);
    } else {
      toast.success(`All ${validData.length} records validated successfully!`);
    }
    
    setUploadStep('processing');
  };

  const processUpload = () => {
    const validData = validateData();
    
    if (validationResults.length > 0) {
      toast.error('Please fix validation errors before processing');
      return;
    }

    setProcessingProgress(0);
    
    // Simulate processing with progress
    const processInterval = setInterval(() => {
      setProcessingProgress(prev => {
        if (prev >= 100) {
          clearInterval(processInterval);
          
          // Simulate upload results
          const results = {
            totalRecords: validData.length,
            successfulUploads: validData.length - Math.floor(validData.length * 0.1), // 90% success rate
            failedUploads: Math.floor(validData.length * 0.1),
            duplicatesFound: Math.floor(validData.length * 0.05),
            newRecords: validData.length - Math.floor(validData.length * 0.05)
          };
          
          setUploadResults(results);
          setUploadStep('complete');
          toast.success('Outstanding data uploaded successfully!');
          
          return 100;
        }
        return prev + 10;
      });
    }, 300);
  };

  const downloadSampleFile = () => {
    const worksheet = XLSX.utils.json_to_sheet(sampleData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Outstanding_Sample');
    XLSX.writeFile(workbook, 'outstanding_sample.xlsx');
    toast.success('Sample file downloaded successfully');
  };

  const resetUpload = () => {
    setUploadStep('upload');
    setUploadedData([]);
    setHeaders([]);
    setColumnMapping({});
    setValidationResults([]);
    setProcessingProgress(0);
    setUploadResults(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const renderUploadStep = () => (
    <Card className="border-0 shadow-sm" style={{ borderRadius: '12px' }}>
      <Card.Body className="text-center py-5">
        <div className="mb-4">
          <i className="fas fa-cloud-upload-alt fa-4x text-primary mb-3"></i>
          <h4>Upload Outstanding Data</h4>
          <p className="text-muted">
            Upload your outstanding data in Excel or CSV format
          </p>
        </div>
        
        <div className="mb-4">
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileUpload}
            accept=".xlsx,.xls,.csv"
            style={{ display: 'none' }}
          />
          <Button 
            variant="primary" 
            size="lg"
            onClick={() => fileInputRef.current?.click()}
          >
            <i className="fas fa-upload me-2"></i>
            Select File to Upload
          </Button>
        </div>
        
        <div className="mb-4">
          <Button 
            variant="outline-secondary"
            onClick={downloadSampleFile}
          >
            <i className="fas fa-download me-2"></i>
            Download Sample Format
          </Button>
        </div>

        <Alert variant="info">
          <h6><i className="fas fa-info-circle me-2"></i>File Requirements:</h6>
          <ul className="text-start mb-0">
            <li>Supported formats: .xlsx, .xls, .csv</li>
            <li>Maximum file size: 10MB</li>
            <li>Required columns: Retailer ID, Retailer Name, Outstanding Amount</li>
            <li>Optional columns: Due Date, Invoice Number, Description</li>
          </ul>
        </Alert>
      </Card.Body>
    </Card>
  );

  const renderMappingStep = () => (
    <div>
      <Card className="border-0 shadow-sm mb-4" style={{ borderRadius: '12px' }}>
        <Card.Body>
          <h5 className="mb-3">
            <i className="fas fa-map me-2"></i>
            Map Your Columns
          </h5>
          <p className="text-muted mb-4">
            Map your file columns to the required fields. Found {uploadedData.length} records.
          </p>
          
          <Row>
            {requiredColumns.map((col) => (
              <Col md={6} className="mb-3" key={col.key}>
                <Form.Group>
                  <Form.Label>
                    {col.label} <span className="text-danger">*</span>
                  </Form.Label>
                  <Form.Select
                    value={columnMapping[col.key] || ''}
                    onChange={(e) => handleColumnMapping(col.key, e.target.value)}
                  >
                    <option value="">Select Column</option>
                    {headers.map((header, index) => (
                      <option key={index} value={header}>
                        {header}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Text className="text-muted">
                    Example: {col.example}
                  </Form.Text>
                </Form.Group>
              </Col>
            ))}
          </Row>
          
          <div className="d-flex justify-content-between">
            <Button variant="secondary" onClick={resetUpload}>
              <i className="fas fa-arrow-left me-2"></i>
              Back
            </Button>
            <Button variant="primary" onClick={processMapping}>
              <i className="fas fa-check me-2"></i>
              Validate Data
            </Button>
          </div>
        </Card.Body>
      </Card>
      
      {/* Preview Data */}
      <Card className="border-0 shadow-sm" style={{ borderRadius: '12px' }}>
        <Card.Body>
          <h6>Data Preview (First 5 records)</h6>
          <div className="table-responsive">
            <Table striped bordered hover size="sm">
              <thead>
                <tr>
                  {headers.map((header, index) => (
                    <th key={index}>{header}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {uploadedData.slice(0, 5).map((row, index) => (
                  <tr key={index}>
                    {headers.map((header, headerIndex) => (
                      <td key={headerIndex}>{row[header]}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </Table>
          </div>
        </Card.Body>
      </Card>
    </div>
  );

  const renderProcessingStep = () => (
    <Card className="border-0 shadow-sm" style={{ borderRadius: '12px' }}>
      <Card.Body>
        <h5 className="mb-4">
          <i className="fas fa-cogs me-2"></i>
          Processing Data
        </h5>
        
        {validationResults.length > 0 && (
          <Alert variant="warning" className="mb-4">
            <h6><i className="fas fa-exclamation-triangle me-2"></i>Validation Errors Found</h6>
            <p>Please fix the following errors before processing:</p>
            <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
              {validationResults.slice(0, 10).map((error, index) => (
                <div key={index} className="mb-2">
                  <strong>Row {error.row}:</strong>
                  <ul className="mb-1">
                    {error.errors.map((err, errIndex) => (
                      <li key={errIndex}>{err}</li>
                    ))}
                  </ul>
                </div>
              ))}
              {validationResults.length > 10 && (
                <small>... and {validationResults.length - 10} more errors</small>
              )}
            </div>
          </Alert>
        )}
        
        {validationResults.length === 0 && processingProgress === 0 && (
          <div className="text-center py-4">
            <i className="fas fa-check-circle fa-3x text-success mb-3"></i>
            <h6>Data Validation Successful</h6>
            <p className="text-muted">
              All {uploadedData.length} records passed validation. Ready to upload.
            </p>
            <Button variant="success" onClick={processUpload}>
              <i className="fas fa-upload me-2"></i>
              Start Upload
            </Button>
          </div>
        )}
        
        {processingProgress > 0 && processingProgress < 100 && (
          <div className="text-center py-4">
            <div className="mb-3">
              <div className="spinner-border text-primary mb-3" role="status">
                <span className="visually-hidden">Processing...</span>
              </div>
              <h6>Uploading Outstanding Data...</h6>
            </div>
            <ProgressBar now={processingProgress} label={`${processingProgress}%`} />
          </div>
        )}
        
        <div className="d-flex justify-content-between mt-4">
          <Button variant="secondary" onClick={() => setUploadStep('mapping')}>
            <i className="fas fa-arrow-left me-2"></i>
            Back to Mapping
          </Button>
          {validationResults.length > 0 && (
            <Button variant="outline-secondary" onClick={() => {
              const errorData = validationResults.map(error => ({
                Row: error.row,
                Errors: error.errors.join(', '),
                'Retailer ID': error.data.retailer_id,
                'Retailer Name': error.data.retailer_name,
                'Outstanding Amount': error.data.outstanding_amount
              }));
              
              const worksheet = XLSX.utils.json_to_sheet(errorData);
              const workbook = XLSX.utils.book_new();
              XLSX.utils.book_append_sheet(workbook, worksheet, 'Validation_Errors');
              XLSX.writeFile(workbook, 'validation_errors.xlsx');
            }}>
              <i className="fas fa-download me-2"></i>
              Download Error Report
            </Button>
          )}
        </div>
      </Card.Body>
    </Card>
  );

  const renderCompleteStep = () => (
    <Card className="border-0 shadow-sm" style={{ borderRadius: '12px' }}>
      <Card.Body className="text-center py-5">
        <div className="mb-4">
          <i className="fas fa-check-circle fa-4x text-success mb-3"></i>
          <h4>Upload Complete!</h4>
          <p className="text-muted">
            Outstanding data has been processed successfully
          </p>
        </div>
        
        {uploadResults && (
          <Row className="g-4 mb-4">
            <Col md={3}>
              <Card className="border-primary">
                <Card.Body className="text-center">
                  <h4 className="text-primary">{uploadResults.totalRecords}</h4>
                  <small>Total Records</small>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="border-success">
                <Card.Body className="text-center">
                  <h4 className="text-success">{uploadResults.successfulUploads}</h4>
                  <small>Successful</small>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="border-warning">
                <Card.Body className="text-center">
                  <h4 className="text-warning">{uploadResults.duplicatesFound}</h4>
                  <small>Duplicates</small>
                </Card.Body>
              </Card>
            </Col>
            <Col md={3}>
              <Card className="border-danger">
                <Card.Body className="text-center">
                  <h4 className="text-danger">{uploadResults.failedUploads}</h4>
                  <small>Failed</small>
                </Card.Body>
              </Card>
            </Col>
          </Row>
        )}
        
        <div className="d-flex justify-content-center gap-2">
          <Button variant="primary" onClick={resetUpload}>
            <i className="fas fa-plus me-2"></i>
            Upload Another File
          </Button>
          <Button variant="outline-primary" href="/distributor/retailers">
            <i className="fas fa-users me-2"></i>
            View Retailers
          </Button>
        </div>
      </Card.Body>
    </Card>
  );

  return (
    <Container fluid className="py-1">
      <div className="d-flex justify-content-between align-items-center mb-4">
        <h5 style={{ color: '#031a6f', fontWeight: '600' }}>
          <i className="fas fa-file-excel me-2"></i>
          Outstanding Excel Upload
        </h5>
        
        {/* Progress Steps */}
        <div className="d-flex align-items-center">
          {['upload', 'mapping', 'processing', 'complete'].map((step, index) => (
            <div key={step} className="d-flex align-items-center">
              <div 
                className={`rounded-circle d-flex align-items-center justify-content-center ${
                  uploadStep === step ? 'bg-primary text-white' : 
                  ['upload', 'mapping', 'processing', 'complete'].indexOf(uploadStep) > index ? 'bg-success text-white' : 'bg-light text-muted'
                }`}
                style={{ width: '32px', height: '32px', fontSize: '14px' }}
              >
                {index + 1}
              </div>
              {index < 3 && (
                <div 
                  className={`${
                    ['upload', 'mapping', 'processing', 'complete'].indexOf(uploadStep) > index ? 'bg-success' : 'bg-light'
                  }`}
                  style={{ width: '40px', height: '2px', margin: '0 8px' }}
                ></div>
              )}
            </div>
          ))}
        </div>
      </div>

      {uploadStep === 'upload' && renderUploadStep()}
      {uploadStep === 'mapping' && renderMappingStep()}
      {uploadStep === 'processing' && renderProcessingStep()}
      {uploadStep === 'complete' && renderCompleteStep()}
    </Container>
  );
};

export default OutstandingUpload;